import { useEffect } from 'react';

import { useMagicIslandController } from './MagicIslandContext';
import type { IslandMode } from './types';

/**
 * Sets the Magic Island's fallback — the mode shown whenever no scrolled
 * section claims the reading line, instead of hiding (e.g. the persistent
 * purchase pill on the mobile product page). Releases on `null` or unmount.
 *
 * - A no-op when there is no provider (feature off), so callers can call it
 *   unconditionally.
 * - `mode` must be referentially stable across renders (wrap it in `useMemo`),
 *   otherwise the effect re-runs every render.
 * - Single-slot: the controller holds one fallback, so only one owner should
 *   set it per island.
 * - A section can still blank the island over the fallback by registering a
 *   `{ kind: 'hidden' }` mode — that claim suppresses the fallback while the
 *   section crosses the reading line.
 */
export const useIslandFallback = (mode: IslandMode | null) => {
    const controller = useMagicIslandController();

    useEffect(() => {
        if (!controller) return;
        controller.setFallback(mode);
        return () => controller.setFallback(null);
    }, [controller, mode]);
};
